/* NIR (numéro de sécurité sociale) — client-side validation + step gating.
 *
 * The SAME predicate (shouldCollectNir) gates the progress-bar spine
 * (helper/inscriptionSteps.ts) AND the real navigation (HomeContent
 * handleNextInscription) so the two can never desynchronize.
 *
 * The mod-97 check MUST stay in sync with the server's
 * kastafiorementalplus-api/src/helpers/nirControlKey.ts — same leading-digit
 * policy ([1278]: 1/2 definitive, 7/8 provisional NIA; historical 3/4 numbers
 * are rejected pending an operator policy decision), same Corsica rule.
 * A passing key is a TYPO FILTER only — it never proves the number belongs
 * to the declarant.
 */
import { AttributeEnum, isPasswordSignInMethod, NirCollectionModeType, ProfileType } from "../types/profile";
import { RoleEnum } from "../types/invitation.types";

export const NIR_LENGTH = 15;

/** Strip user formatting (spaces, dots, dashes) and upcase Corsica letters. */
export const normalizeNir = (raw?: string): string =>
  (raw ?? "").replace(/[\s.-]/g, "").toUpperCase();

/**
 * Control key of a 13-char NIR body. Corsica: the département occupies body
 * indexes 5–6; '2A' → letter becomes '0' then subtract 1 000 000 before the
 * modulo, '2B' → '0' and 2 000 000 (INSEE rule). Département 99 (born abroad)
 * is plain digits. The 13-digit body (≤ 9.9e12) is exact in a JS Number.
 * Returns null when the body is not a valid NIR body.
 */
export const computeNirKey = (body: string): number | null => {
  if (body.length !== 13) return null;
  let digits = body;
  let corsicaOffset = 0;
  if (body[6] === "A") {
    digits = body.slice(0, 6) + "0" + body.slice(7);
    corsicaOffset = 1_000_000;
  } else if (body[6] === "B") {
    digits = body.slice(0, 6) + "0" + body.slice(7);
    corsicaOffset = 2_000_000;
  }
  if (!/^\d{13}$/.test(digits)) return null;
  return 97 - ((Number(digits) - corsicaOffset) % 97);
};

/** Full client-side NIR check (15 chars after normalization, mod-97 key). */
export const isValidNir = (raw?: string): boolean => {
  const nir = normalizeNir(raw);
  if (nir.length !== NIR_LENGTH) return false;
  if (!/^\d{5}(?:\d{2}|2A|2B)\d{6}\d{2}$/.test(nir)) return false;
  if (!/^[1278]/.test(nir)) return false;
  const expected = computeNirKey(nir.slice(0, 13));
  return expected !== null && expected === Number(nir.slice(13));
};

/** Effective collection mode for a role (absent/unknown ⇒ HIDDEN, fail-safe). */
export const getNirCollectionMode = (role?: ProfileType): NirCollectionModeType => {
  const mode = role?.attributes?.nir_collection_mode;
  return mode === "OPTIONAL" || mode === "REQUIRED" ? mode : "HIDDEN";
};

/**
 * The NIR step only renders for a self-registering, password-authenticated
 * SUBJECT (the parent declares their OWN number). Mirrors the Diapason
 * control's gating and defends against a stale REQUIRED left on a role later
 * switched to no-signin / pre-created / secret-questions.
 */
export const shouldCollectNir = (role?: ProfileType): boolean =>
  role?.name === RoleEnum.SUBJECT &&
  role?.attributes?.[AttributeEnum.IS_AUTHENTICATIVE] !== false &&
  isPasswordSignInMethod(role?.access?.signInMode) &&
  getNirCollectionMode(role) !== "HIDDEN";
